/**
 * SaaS workspace signup is offered on the platform apex host only
 * (not on tenant subdomains like acme.clinchsa.co.za).
 *
 * Hostname model:
 * - `platform` is the tenant *schema/name*, not a DNS label.
 * - Platform tenant Host = apex (`clinchsa.co.za` / `staging.clinchsa.co.za`).
 * - Other workspaces = `{slug}.{PLATFORM_DOMAIN}` (one label only).
 * - `platform.{PLATFORM_DOMAIN}` is NOT a product URL — do not invent it.
 */

const PLATFORM_DOMAIN = (
  process.env.VUE_APP_SAAS_PLATFORM_DOMAIN || 'clinchsa.co.za'
).toLowerCase()

export function getSaasPlatformDomain() {
  return PLATFORM_DOMAIN
}

/**
 * Signup / marketing apex only.
 * Local: localhost, 127.0.0.1, platform.localhost.
 * Staging/prod: PLATFORM_DOMAIN and www.{PLATFORM_DOMAIN}.
 * Never treat platform.{PLATFORM_DOMAIN} as signup (that host is unused).
 */
export function isSaasSignupHost(host) {
  if (typeof window === 'undefined' && !host) return false
  const h = String(host || window.location.hostname || '').toLowerCase()
  if (!h) return false
  if (h === 'localhost' || h === '127.0.0.1') return true
  if (h === 'platform.localhost') return true
  if (h === PLATFORM_DOMAIN || h === `www.${PLATFORM_DOMAIN}`) return true
  return false
}

/**
 * Tenant workspace host — one label under PLATFORM_DOMAIN (or .platform.localhost in DEBUG).
 * False on the apex: that Host *is* the platform tenant (schema `platform`).
 * True only for e.g. acme.clinchsa.co.za / acme.staging.clinchsa.co.za / acme.platform.localhost.
 */
export function isTenantWorkspaceHost(host) {
  if (typeof window === 'undefined' && !host) return false
  const h = String(host || window.location.hostname || '').toLowerCase()
  if (!h || h === 'localhost' || h === '127.0.0.1') return false
  if (h === 'platform.localhost') return false
  if (h.endsWith('.platform.localhost')) {
    const slug = h.slice(0, -'.platform.localhost'.length)
    return Boolean(slug && !slug.includes('.'))
  }
  if (h === PLATFORM_DOMAIN || h === `www.${PLATFORM_DOMAIN}`) return false
  if (h.endsWith(`.${PLATFORM_DOMAIN}`)) {
    const slug = h.slice(0, -(PLATFORM_DOMAIN.length + 1))
    // One DNS label only — rejects acme.staging when PLATFORM_DOMAIN is clinchsa.co.za
    return Boolean(slug && !slug.includes('.'))
  }
  return false
}

export const UNKNOWN_WORKSPACE_PATH = '/workspace-not-found'

const CLINCH_APEX_ONLY_EXACT = new Set([
  '/',
  '/pricing',
  '/register',
  '/blog',
  '/contact',
  '/start',
  '/businesses',
  '/products',
  UNKNOWN_WORKSPACE_PATH,
])

/**
 * Origin of the Clinch marketing / platform tenant SPA (not a workspace subdomain).
 * Local wildcard hosts (`*.platform.localhost`) keep the current protocol + port.
 */
export function getSaasPlatformOrigin(loc) {
  const locationRef = loc || (typeof window !== 'undefined' ? window.location : null)
  const protocol = locationRef?.protocol || 'https:'
  const hostname = String(locationRef?.hostname || '').toLowerCase()
  const port = locationRef?.port || ''
  if (hostname === 'platform.localhost' || hostname.endsWith('.platform.localhost')) {
    return `${protocol}//platform.localhost${port ? `:${port}` : ''}`
  }
  return `${protocol}//${PLATFORM_DOMAIN}`
}

export function unknownWorkspaceNotFoundUrl(missingHost, loc) {
  const origin = getSaasPlatformOrigin(loc)
  const host = String(missingHost || '').trim().toLowerCase()
  if (!host) return `${origin}${UNKNOWN_WORKSPACE_PATH}`
  return `${origin}${UNKNOWN_WORKSPACE_PATH}?host=${encodeURIComponent(host)}`
}

/**
 * Clinch marketing / marketplace pages. Tenant workspace hosts send visitors to login instead.
 * Signup completion (`/start/complete`) and `/welcome` stay available on tenant hosts.
 */
export function isClinchApexOnlyPublicPath(path) {
  const p = String(path || '').split('?')[0]
  if (!p || p === '/') return true
  if (CLINCH_APEX_ONLY_EXACT.has(p)) return true
  if (p.startsWith('/businesses/') || p.startsWith('/products/')) return true
  if (p.startsWith('/start/') && p !== '/start/complete') return true
  return false
}
